import { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Paperclip, Trash2, X } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { SelectNative } from "../ui/select-native";
import { CurrencyHint } from "../CurrencyHint";
import {
  createPolicy,
  policyPdfUrl,
  removePolicyPdf,
  updatePolicy,
  uploadPolicyPdf,
} from "../../lib/api";
import { normalizeDetail } from "../../lib/errors";
import {
  FREQUENCIES,
  LIFE_LIKE_TYPES,
  POLICY_TYPES,
  REMINDER_CHANNELS,
} from "./policyShared";

/**
 * Add / Edit policy modal. Extracted from PoliciesPage.jsx during 08-Feb refactor.
 * Contains the ~340-line form + PDF-attachment sub-panel and is the largest child of the
 * Policies page. All behaviour (create → then upload PDF if provided; edit → save then
 * optionally replace/remove the attached PDF) is unchanged.
 *
 * Props:
 *   - mode: "create" | "edit"
 *   - initial: existing policy (only used when mode === "edit")
 *   - onClose: dismiss handler
 *   - onSaved: called after a successful save + optional PDF upload
 */

const EMPTY_FORM = {
  type: "term_life",
  name: "",
  insurer: "",
  policy_number: "",
  plan_number: "",
  sum_assured: "",
  premium_amount: "",
  premium_frequency: "annually",
  policy_term_years: "",
  premium_paying_term_years: "",
  start_date: "",
  end_date: "",
  maturity_date: "",
  next_renewal_date: "",
  reminder_enabled: true,
  reminder_channel: "email",
  // 1-day-before is mandatory (checkbox is disabled + always checked in the UI, and
  // enforced server-side in schemas.PolicyIn._valid_enums). 7/15/30 are optional.
  reminder_days_advance: [1],
  notes: "",
};

function policyToForm(p) {
  return {
    type: p.type,
    name: p.name,
    insurer: p.insurer || "",
    policy_number: p.policy_number || "",
    plan_number: p.plan_number || "",
    sum_assured: p.sum_assured ?? "",
    premium_amount: p.premium_amount ?? "",
    premium_frequency: p.premium_frequency,
    policy_term_years: p.policy_term_years ?? "",
    premium_paying_term_years: p.premium_paying_term_years ?? "",
    start_date: p.start_date || "",
    end_date: p.end_date || "",
    maturity_date: p.maturity_date || "",
    next_renewal_date: p.next_renewal_date || "",
    reminder_enabled: !!p.reminder_enabled,
    reminder_channel: p.reminder_channel || "email",
    reminder_days_advance: Array.isArray(p.reminder_days_advance) && p.reminder_days_advance.length
      ? p.reminder_days_advance
      : [1],
    notes: p.notes || "",
  };
}

function toApiPayload(form) {
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  const strOrNull = (v) => (v && String(v).trim() ? String(v).trim() : null);
  return {
    type: form.type,
    name: form.name.trim(),
    insurer: strOrNull(form.insurer),
    policy_number: strOrNull(form.policy_number),
    plan_number: strOrNull(form.plan_number),
    sum_assured: numOrNull(form.sum_assured),
    premium_amount: numOrNull(form.premium_amount),
    premium_frequency: form.premium_frequency,
    policy_term_years: numOrNull(form.policy_term_years),
    premium_paying_term_years: numOrNull(form.premium_paying_term_years),
    start_date: strOrNull(form.start_date),
    end_date: strOrNull(form.end_date),
    maturity_date: strOrNull(form.maturity_date),
    next_renewal_date: strOrNull(form.next_renewal_date),
    reminder_enabled: !!form.reminder_enabled,
    reminder_channel: form.reminder_channel,
    reminder_days_advance: Array.from(new Set([1, ...(form.reminder_days_advance || [])])).sort((a, b) => a - b),
    notes: strOrNull(form.notes),
  };
}

export function PolicyDialog({ initial, mode, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? policyToForm(initial) : EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [removingPdf, setRemovingPdf] = useState(false);

  function set(field) {
    return (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter a policy name.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = toApiPayload(form);
      let saved;
      if (mode === "edit" && initial) {
        saved = await updatePolicy(initial.id, payload);
      } else {
        saved = await createPolicy(payload);
      }
      // Upload the PDF (if any) after the record exists. A storage failure surfaces as a
      // toast but doesn't roll back the policy save — the member can retry later.
      if (pdfFile) {
        try {
          await uploadPolicyPdf(saved.id, pdfFile);
        } catch (err) {
          toast.error(
            normalizeDetail(err.response?.data?.detail, "Policy saved, but PDF upload failed."),
          );
        }
      }
      toast.success(mode === "edit" ? "Policy updated." : "Policy added.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save the policy."));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemoveExistingPdf() {
    if (!initial?.has_pdf) return;
    if (!window.confirm("Remove the attached PDF? You can re-upload later.")) return;
    setRemovingPdf(true);
    try {
      await removePolicyPdf(initial.id);
      toast.success("PDF removed.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't remove the PDF."));
    } finally {
      setRemovingPdf(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="policy-dialog"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <h3 className="font-serif text-xl text-text">
            {mode === "edit" ? "Edit policy" : "Add a policy"}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"
            data-testid="policy-dialog-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-type">Type</Label>
              <SelectNative id="p-type" value={form.type} onChange={set("type")} data-testid="policy-form-type">
                {POLICY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </SelectNative>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-name">Policy name *</Label>
              <Input
                id="p-name"
                value={form.name}
                onChange={set("name")}
                placeholder="HDFC Life Click 2 Protect"
                required
                data-testid="policy-form-name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-insurer">Insurer</Label>
              <Input id="p-insurer" value={form.insurer} onChange={set("insurer")} placeholder="HDFC Life" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-num">Policy number</Label>
              <Input id="p-num" value={form.policy_number} onChange={set("policy_number")} placeholder="Encrypted at rest" />
            </div>
            {form.type === "life_insurance" && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="p-plan">Plan / Table number</Label>
                <Input
                  id="p-plan"
                  value={form.plan_number}
                  onChange={set("plan_number")}
                  placeholder="e.g. LIC 914, Jeevan Anand"
                  data-testid="policy-form-plan-number"
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-sum">Sum assured (₹)</Label>
              <Input
                id="p-sum"
                type="number"
                min="0"
                step="1000"
                value={form.sum_assured}
                onChange={set("sum_assured")}
                data-testid="policy-form-sum-assured"
              />
              <CurrencyHint value={form.sum_assured} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-prem">Premium amount (₹)</Label>
              <Input id="p-prem" type="number" min="0" step="100" value={form.premium_amount} onChange={set("premium_amount")} />
              <CurrencyHint value={form.premium_amount} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-freq">Premium frequency</Label>
              <SelectNative id="p-freq" value={form.premium_frequency} onChange={set("premium_frequency")}>
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </SelectNative>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-term">Policy term (years)</Label>
              <Input id="p-term" type="number" min="0" max="100" step="1" value={form.policy_term_years} onChange={set("policy_term_years")} />
            </div>
            {LIFE_LIKE_TYPES.has(form.type) && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="p-ppt">Premium Paying Term (years)</Label>
                <Input
                  id="p-ppt"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={form.premium_paying_term_years}
                  onChange={set("premium_paying_term_years")}
                  placeholder="e.g. 15"
                  data-testid="policy-form-ppt"
                />
                <p className="text-xs text-muted">
                  How many years you actually pay premiums for. Often shorter than the policy term — e.g. a 55-year LIC policy with a 15-year PPT.
                </p>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-start">Start date <span className="text-gold">*</span></Label>
              <Input id="p-start" type="date" value={form.start_date} onChange={set("start_date")} data-testid="policy-form-start-date" />
              <p className="text-xs text-muted">
                Also acts as the first premium date. Subsequent premiums roll forward by frequency until PPT ends.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-end">End date</Label>
              <Input id="p-end" type="date" value={form.end_date} onChange={set("end_date")} />
            </div>
            {form.type === "life_insurance" && (
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="p-maturity">Maturity date</Label>
                <Input
                  id="p-maturity"
                  type="date"
                  value={form.maturity_date}
                  onChange={set("maturity_date")}
                  data-testid="policy-form-maturity-date"
                />
                <p className="text-xs text-muted">
                  When the policy pays out the sum assured + bonuses. Usually = start date + policy term.
                </p>
              </div>
            )}
            {/* `next_renewal_date` input was removed on 08-Feb-2026 — the premium schedule
                now derives purely from start_date + frequency + PPT. The backend still stores
                the field for backward-compat with existing rows but no longer prompts for it. */}

            <div className="flex flex-col gap-2 rounded-lg border border-border bg-elevated/40 p-3 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.reminder_enabled}
                  onChange={set("reminder_enabled")}
                  data-testid="policy-form-reminder-enabled"
                />
                <span className="text-text">Send me renewal reminders</span>
              </label>
              {form.reminder_enabled && (
                <div className="flex flex-col gap-3 pl-6">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="p-channel">Reminder channel</Label>
                    <SelectNative id="p-channel" value={form.reminder_channel} onChange={set("reminder_channel")}>
                      {REMINDER_CHANNELS.filter((c) => c.value !== "none").map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </SelectNative>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Advance nudges</Label>
                    <div className="flex flex-wrap gap-3 text-xs text-muted">
                      {/* 1-day-before is mandatory: checkbox is checked + disabled so the
                         user sees it's always on but can't remove it (server enforces it too). */}
                      <label className="flex items-center gap-1.5 opacity-70">
                        <input type="checkbox" checked disabled data-testid="policy-form-days-1" />
                        <span>1 day before <span className="text-[10px] uppercase text-gold">(default)</span></span>
                      </label>
                      {[7, 15, 30].map((d) => (
                        <label key={d} className="flex items-center gap-1.5 text-text">
                          <input
                            type="checkbox"
                            checked={(form.reminder_days_advance || []).includes(d)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setForm((prev) => {
                                const cur = new Set(prev.reminder_days_advance || [1]);
                                if (checked) cur.add(d); else cur.delete(d);
                                cur.add(1);
                                return { ...prev, reminder_days_advance: Array.from(cur).sort((a, b) => a - b) };
                              });
                            }}
                            data-testid={`policy-form-days-${d}`}
                          />
                          <span>{d} days before</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="p-notes">Notes</Label>
              <textarea
                id="p-notes"
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Nominee, hospital network, exclusions…"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>

            {/* PDF attachment: uploaded after policy-save so the record exists to attach to. */}
            <div className="flex flex-col gap-2 rounded-lg border border-border bg-elevated/40 p-3 sm:col-span-2">
              <Label className="flex items-center gap-1.5 text-sm">
                <Paperclip className="h-3.5 w-3.5 text-gold" /> Attach policy PDF
                <span className="text-xs font-normal text-muted">(optional, max 20 MB)</span>
              </Label>
              {initial?.has_pdf && !pdfFile && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gold/25 bg-gold/5 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 flex-shrink-0 text-gold" />
                    <span className="truncate text-text">{initial.pdf_filename}</span>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <a
                      href={policyPdfUrl(initial.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-text hover:border-gold hover:text-gold"
                      data-testid={`policy-form-pdf-download-${initial.id}`}
                    >
                      <Download className="h-3 w-3" /> Download
                    </a>
                    <button
                      type="button"
                      onClick={handleRemoveExistingPdf}
                      disabled={removingPdf}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted hover:border-red-500 hover:text-red-400"
                      data-testid="policy-form-pdf-remove"
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                disabled={submitting}
                className="text-xs text-muted file:mr-2 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-elevated file:px-3 file:py-1.5 file:text-xs file:text-text hover:file:border-gold hover:file:text-gold"
                data-testid="policy-form-pdf-input"
              />
              {pdfFile && (
                <p className="text-xs text-gold">
                  {initial?.has_pdf ? "This will replace the current PDF." : "Will be uploaded after the policy is saved."}
                  {" "}<span className="text-muted">{pdfFile.name}</span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="policy-form-submit">
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Add policy"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
